import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react'
import { MAX_VISIBLE_BRANDS_IN_MODAL } from '../../../constants/appConfig'
import doneTickYellowAnimation from '../../../assets/lottie/doneTickYellow.json'
import doneTickAnimation from '../../../assets/lottie/doneTick.json'
import { Loader, Modal } from '@grofers/dock-design-kit'
import type { StatusModalProps } from './StatusModal.types'
import { ExpandableBrandList } from '../../ui/ExpandableBrandList'
// Lazy load Lottie component
const Lottie = lazy(() => import('lottie-react'))

const StatusModal: React.FC<StatusModalProps> = ({
  isOpen = false,
  onClose = () => {},
  title = '',
  description = '',
  additionalText = '',
  brands = [],
  dismissable = true,
  variant = 'pending',
}) => {
  const [isLottiePrefetched, setIsLottiePrefetched] = useState(false)
  const [isBrandsExpanded, setIsBrandsExpanded] = useState(false)

  // Prefetch Lottie when modal is about to open
  useEffect(() => {
    if (isOpen && !isLottiePrefetched) {
      // Prefetch the Lottie module
      import('lottie-react').then(() => {
        setIsLottiePrefetched(true)
      })
    }
  }, [isOpen, isLottiePrefetched])

  // Reset expanded state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsBrandsExpanded(false)
    }
  }, [isOpen])

  const handleExpandBrands = useCallback(() => {
    setIsBrandsExpanded(true)
  }, [])

  const handleCollapseBrands = useCallback(() => {
    setIsBrandsExpanded(false)
  }, [])

  const animationData = useMemo(
    () => (variant === 'approved' ? doneTickAnimation : doneTickYellowAnimation),
    [variant]
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      showClose={dismissable}
      closeOnBackdropClick={dismissable}
      aria-label="Status modal"
    >
      <div className="mb-3 flex justify-center pt-6">
        <div className="h-20 w-20">
          <Suspense fallback={isLottiePrefetched ? null : <Loader showLoader />}>
            <Lottie
              animationData={animationData}
              loop={false}
              autoplay={true}
              className="h-full w-full"
            />
          </Suspense>
        </div>
      </div>

      <div className="mb-1 text-center text-xl font-bold text-gray-900">{title}</div>

      <div className="text-center text-xs font-normal text-gray-600">{description}</div>
      {brands?.length > 0 && (
        <div className="mt-3 flex flex-col items-center gap-2">
          <ExpandableBrandList
            brands={brands}
            isExpanded={isBrandsExpanded}
            onExpand={handleExpandBrands}
            onCollapse={handleCollapseBrands}
            maxVisible={MAX_VISIBLE_BRANDS_IN_MODAL}
            className="justify-center"
          />
        </div>
      )}
      {additionalText && (
        <div className="mt-3 text-center text-xs font-normal text-gray-600">{additionalText}</div>
      )}
    </Modal>
  )
}

export default StatusModal
